'use client';

import { Heading, Price } from '@petsmart-ui/sparky';

import { decodeEntityString } from '@libs/algolia';
import { useDecisions } from '@libs/common-next';
import type { Product } from '@libs/common-types';

import { PricePolicyDrawer } from '../PricePolicyDrawer';
import { getPriceDisplay } from '../product-details';
import { ResponsivePicture } from '../ResponsivePicture';
import { useMiniPdpDrawer } from './model/use-mini-pdp-drawer';

export function MiniPdpProductInfo({ product }: Readonly<{ product: Product }>) {
    const firstImage = product.media.find(item => item.type === 'image');
    const productName = decodeEntityString(product.name);
    const priceDisplay = getPriceDisplay(product);
    const isDrawerOpen = useMiniPdpDrawer(state => state.isOpen);
    const close = useMiniPdpDrawer(state => state.close);
    const productUrl = useMiniPdpDrawer(state => state.productUrl);
    const isAutoshipMiniPdpEnabled = useDecisions('as_mini_pdp');
    const linkUrl = isAutoshipMiniPdpEnabled ? productUrl : undefined;

    const image = firstImage && (
        <ResponsivePicture src={firstImage.url} alt={productName} size={{ width: 80, height: 80 }} />
    );

    return (
        <div className="mini-pdp-drawer__product-info">
            {image && (
                <div className="mini-pdp-drawer__product-image">
                    {linkUrl ? (
                        <a
                            href={linkUrl}
                            className="mini-pdp-drawer__product-link"
                            tabIndex={-1}
                            data-testid="mini-pdp-product-image-link"
                        >
                            {image}
                        </a>
                    ) : (
                        image
                    )}
                </div>
            )}
            <div className="mini-pdp-drawer__product-content">
                <Heading tagName="h3" className="sparky-u-theme-typography-body" data-testid="mini-pdp-product-name">
                    {linkUrl ? (
                        <a
                            href={linkUrl}
                            className="mini-pdp-drawer__product-link mini-pdp-drawer__product-link--title"
                            data-testid="mini-pdp-product-name-link"
                        >
                            {productName}
                        </a>
                    ) : (
                        productName
                    )}
                </Heading>
                <div className="mini-pdp-drawer__price">
                    <Price
                        values={priceDisplay.values}
                        variant={priceDisplay.variant}
                        size="xl"
                        className="mini-pdp-drawer__product-price"
                        data-testid="mini-pdp-product-price"
                    />
                    {priceDisplay.variant === 'disabled' && (
                        <PricePolicyDrawer showBackButton forceClosed={!isDrawerOpen} onCloseAll={close} />
                    )}
                </div>
            </div>
        </div>
    );
}
